/* eslint-disable import/max-dependencies */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { usePageTitle } from '../config';
/* eslint-disable import/no-extraneous-dependencies*/
import Cookies from 'js-cookie';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Footer } from '@im/ui-lib/Footer';
import { IngramLogo } from '@im/ui-lib/icons/IngramLogo';
import { CulturePicker } from '@im/ui-lib/CulturePicker/CulturePicker';
import { useReusableContents } from './use-reusable-contents';
import { useApiQuery, useApiPatch } from '../query';
import htmlparse from 'html-react-parser';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import {
  useLocalization,
  useLocalizationContext,
  useTranslate,
} from '@im/react-localization';
import { fetchIpAdrress } from './user-info.hooks';
import { useFeatureFlag } from '../feature-flag';
import { useOktaAuth } from '@okta/okta-react';
import { PageLoadTimeProvider } from '../provider/dd-custom-actions';
import { useCopyrightLinks } from './useCopyrightLinks';

export const TermsAndConditions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const oktaContext = useOktaAuth();
  const { formatDate } = useLocalization();
  const translate = useTranslate();
  const { locale } = useLocalizationContext();
  const apiPatch = useApiPatch();
  const copyRightLinks = useCopyrightLinks();
  const profileData = useProfileData();
  const termsData = useReusableContents('Terms and conditions');
  const [checked, setChecked] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const { isReady, value: BO_Keys } = useFeatureFlag(['Enable_User_IP_Update']);
  const [disableAction, setDisableAction] = useState(false);

  const { updatePageTitle } = usePageTitle();
  updatePageTitle(translate('Title.TnC'));

  useEffect(() => {
    termsData.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const url = isNewUser ? '/welcome-video' : '/my/dashboard';

  useEffect(() => {
    if (
      termsData.status === 'error' ||
      termsData.data?.reusableContents.length === 0
    ) {
      // eslint-disable-next-line no-console
      console.log('[TnC] data is missing');
      navigate(url);
    } else if (profileData.status === 'error') {
      // eslint-disable-next-line no-console
      console.log('[User-Profile] data is missing');
      navigate(url);
    } else if (
      profileData.status === 'success' &&
      termsData.status === 'success'
    ) {
      if (profileData.data.dateLastTermsConditionsAcceptance) {
        if (
          termsData.data.reusableContents.length > 0 &&
          termsData.data.reusableContents[0].documentModifiedWhen
        ) {
          const acceptanceDate = new Date(
            profileData.data.dateLastTermsConditionsAcceptance
          );
          const modifiedDate = new Date(
            termsData.data.reusableContents[0].documentModifiedWhen
          );
          if (acceptanceDate > modifiedDate) {
            navigate(url);
          }
        }
      } else {
        setIsNewUser(true);
      }
    }
  }, [termsData, profileData, navigate, url]);

  const handleChange = (e: any) => {
    setChecked(e.target.checked);
  };
  const handleAccept = async () => {
    let clientIp = '';
    setDisableAction(true);
    if (isReady && BO_Keys['Enable_User_IP_Update'] === 'true') {
      clientIp = await fetchIpAdrress();
    }
    apiPatch(`/account-management/v1/users/terms_conditions_acceptance`, {
      clientIp,
    })
      .then((res) => {
        if (res) {
          const state = location.state as {
            isCepWhitelisted: boolean;
          };
          setDisableAction(false);
          if (state && !state.isCepWhitelisted) {
            Cookies.remove('IMOnline_CEP');
            window.location.href = `${window.location.origin}/site/login/idplogin`;
          } else {
            profileData.refetch();
            navigate(url);
          }
        }
      })
      .catch((err) => {
        setDisableAction(false);
        console.error(err);
      });
  };

  const handleDecline = async () => {
    const postLogoutRedirectUri = `${globalThis.location.origin}/cep`;
    try {
      localStorage.removeItem('okta-token-storage');
      navigate('/logout');
      await oktaContext.oktaAuth.signOut({ postLogoutRedirectUri });
    } catch (err) {
      console.error('logout', err);
      throw err;
    }
  };

  return termsData.status !== 'success' ? (
    <CenterLoader />
  ) : (
    <PageLoadTimeProvider>
      <ScreenContainer>
        <HeaderContainer>
          <IngramLogo />
          <CulturePicker />
        </HeaderContainer>
        <MainContainer>
          <Title>{translate('TnC.HeaderTitle')}</Title>
          <Text>{translate('TnC.HeaderText')}</Text>
          {termsData.data?.reusableContents?.length > 0 && (
            <BoldText>
              {translate('TnC.SalesTermsAndConditions')}{' '}
              <span style={{ fontSize: '12px' }}>
                {'('}
                {translate('TnC.LastUpdated')}
                {` ${formatDate(
                  new Date(
                    termsData.data?.reusableContents[0]?.documentModifiedWhen
                  ),
                  'medium'
                )})`}
              </span>
            </BoldText>
          )}
          {termsData.data?.reusableContents?.length > 0 && (
            <TextContainer id="content">
              {htmlparse(termsData.data?.reusableContents[0]?.content)}
            </TextContainer>
          )}
          {termsData.data?.reusableContents?.length === 0 && (
            <AlertText>{translate('TnC.ContentMissingMessage')}</AlertText>
          )}
        </MainContainer>
        <BottomContainer>
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={handleChange} />}
            label={translate('TnC.ConfirmText')}
          />
          <ButtonContainer>
            <Button
              onClick={() => handleDecline()}
              sx={{ marginLeft: 'auto' }}
              variant="outlined"
            >
              {translate('TnC.Decline')}
            </Button>
            <Button
              disabled={!checked || !isReady || disableAction}
              onClick={() => handleAccept()}
              sx={{ marginLeft: '24px' }}
              variant="contained"
            >
              {translate('TnC.Accept')}
            </Button>
          </ButtonContainer>
        </BottomContainer>
        <FooterContainer>
          <Footer copyRightLinks={copyRightLinks} />
        </FooterContainer>
      </ScreenContainer>
    </PageLoadTimeProvider>
  );
};

type TCAcceptanceDateTO = {
  dateLastTermsConditionsAcceptance: string | null;
};

function useProfileData() {
  return useApiQuery<TCAcceptanceDateTO>(`/account-management/v1/users/me`);
}

const ScreenContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(4, 4),
}));

const MainContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 88,
  marginRight: 88,
  marginTop: 36,
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 400,
  marginBottom: 8,
}));

const Text = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  marginBottom: 45,
}));

const BoldText = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 24,
}));

const TextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // height: '100%',
  height: '400px',
  marginRight: '40px',
  overflow: 'auto',
  paddingRight: '30px',
}));

const BottomContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '88px',
  marginRight: '128px',
  marginTop: 'auto',
  paddingBottom: '40px',
  paddingTop: '45px',
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
}));

const FooterContainer = styled('div')(({ theme }) => ({
  marginLeft: 56,
  marginRight: 56,
  marginTop: 'auto',
}));

const AlertText = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 400,
  marginBottom: 8,
  textAlign: 'center',
}));
